import React, { useEffect, useState, useMemo } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { Users, Shield, ShieldCheck, Search, Send, X, Mail, Webhook, UserPlus } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "beta", label: "Beta" },
    { id: "founding", label: "Founding" },
    { id: "trialing", label: "Trialing" },
    { id: "cancelled", label: "Cancelled" },
    { id: "expired", label: "Expired" },
    { id: "admin_granted", label: "Admin-granted" },
    { id: "pending", label: "Pending stub" },
    { id: "admin", label: "Admins" },
];

// Status options surfaced in the admin manual-controls UI. Backend keeps the
// authoritative list — these mirror it. `beta`, `founding`, `early-access`
// grant full archive access; non-active states are surfaced for grace
// banners only.
const STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "beta", label: "Beta" },
    { value: "founding", label: "Founding" },
    { value: "early-access", label: "Early access" },
    { value: "trialing", label: "Trialing" },
    { value: "paused", label: "Paused" },
    { value: "cancelled", label: "Cancelled" },
    { value: "expired", label: "Expired" },
    { value: "failed", label: "Failed" },
];

function classifyUser(u) {
    if (u.role === "admin") return "admin";
    const s = (u.subscription_status || "").toLowerCase();
    if (!u.email || u.email.endsWith("@pending.local")) return "pending";
    if (s === "active") return "active";
    if (s === "beta") return "beta";
    if (s === "founding") return "founding";
    if (s === "trialing") return "trialing";
    if (s === "cancelled") return "cancelled";
    if (s === "expired" || s === "failed") return "expired";
    if (u.is_member && !u.shopify_customer_id) return "admin_granted";
    return null;
}

function formatTime(iso, opts = { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, opts); } catch { return iso; }
}

const SUB_STATUS_COLOR = {
    active: "text-[#56CCF2]",
    beta: "text-[#A78BFA]",
    founding: "text-[#F4B731]",
    "early-access": "text-[#A78BFA]",
    trialing: "text-[#F4B731]",
    cancelled: "text-[#94A3B8]",
    expired: "text-[#FB7185]",
    failed: "text-[#FB7185]",
    paused: "text-[#94A3B8]",
};

export default function AdminMembers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [savingId, setSavingId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showCreate, setShowCreate] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/users")
            .then((r) => setUsers(r.data || []))
            .catch(() => toast.error("Failed to load members"))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const toggle = async (u) => {
        setSavingId(u.id);
        try {
            const { data } = await api.put(`/admin/users/${u.id}/membership`, { is_member: !u.is_member });
            setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
            toast.success(data.is_member ? `${u.email} is now a member` : `${u.email} member access removed`);
        } catch {
            toast.error("Failed to update membership");
        } finally {
            setSavingId(null);
        }
    };

    const setStatus = async (u, nextStatus) => {
        setSavingId(u.id);
        try {
            const { data } = await api.put(`/admin/users/${u.id}/membership`, { subscription_status: nextStatus });
            setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
            // Keep the detail drawer in sync so the dropdown reflects the new value.
            setSelected((prev) => (prev && prev.user?.id === u.id ? { ...prev, user: { ...prev.user, ...data } } : prev));
            toast.success(`${u.email} → ${nextStatus}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update status");
        } finally {
            setSavingId(null);
        }
    };

    const sendMagicLink = async (u) => {
        try {
            await api.post(`/admin/users/${u.id}/magic-link`);
            toast.success(`Magic link sent to ${u.email}`);
        } catch {
            toast.error("Failed to send magic link");
        }
    };

    const openDetail = (u) => {
        api.get(`/admin/users/${u.id}/detail`)
            .then((r) => setSelected(r.data))
            .catch(() => toast.error("Failed to load member detail"));
    };

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return users.filter((u) => {
            if (filter !== "all") {
                if (filter === "admin" && u.role !== "admin") return false;
                else if (filter !== "admin" && classifyUser(u) !== filter) return false;
            }
            if (!ql) return true;
            return (
                (u.email || "").toLowerCase().includes(ql) ||
                (u.name || "").toLowerCase().includes(ql) ||
                (u.shopify_customer_id || "").toLowerCase().includes(ql)
            );
        });
    }, [users, q, filter]);

    const counts = useMemo(() => {
        const c = { total: users.length, active: 0, trialing: 0, cancelled: 0 };
        users.forEach((u) => {
            const k = classifyUser(u);
            if (k === "active") c.active++;
            if (k === "trialing") c.trialing++;
            if (k === "cancelled") c.cancelled++;
        });
        return c;
    }, [users]);

    return (
        <AdminLayout>
            <div className="p-8 sm:p-10 max-w-6xl">
                <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-3">
                            <Users className="w-3.5 h-3.5" />
                            Operations · Members
                        </div>
                        <h1 className="text-3xl font-bold text-white tracking-tight">Member console</h1>
                        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
                            {counts.total} accounts · {counts.active} active · {counts.trialing} trialing · {counts.cancelled} cancelled.
                            Click a row for full subscription history, magic-link funnel and webhook trail.
                        </p>
                    </div>
                    <button
                        data-testid="admin-members-add"
                        onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2F80ED] hover:bg-[#1E6FE0] text-white text-sm font-semibold tracking-wide transition-colors"
                    >
                        <UserPlus className="w-4 h-4" />
                        Add member
                    </button>
                </header>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id}
                            data-testid={`member-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] transition-colors ${filter === f.id ? "bg-[#2F80ED] text-white" : "bg-[#111827] border border-[#243044] text-[#94A3B8] hover:text-white"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-3 bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 mb-6 focus-within:border-[#2F80ED]">
                    <Search className="w-4 h-4 text-[#64748B] shrink-0" />
                    <input
                        data-testid="admin-members-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by email · name · Shopify customer id…"
                        className="bg-transparent border-0 outline-none text-sm text-white placeholder-[#64748B] flex-1"
                    />
                </div>

                <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden">
                    {loading ? (
                        <div className="px-6 py-10 text-center text-sm text-[#64748B]">Loading members…</div>
                    ) : filtered.length === 0 ? (
                        <div className="px-6 py-10 text-center text-sm text-[#64748B]">No accounts match these filters.</div>
                    ) : (
                        <ul className="divide-y divide-[#243044]" data-testid="admin-members-list">
                            {filtered.map((u) => {
                                const subColor = SUB_STATUS_COLOR[(u.subscription_status || "").toLowerCase()] || "text-[#94A3B8]";
                                return (
                                    <li key={u.id} data-testid={`admin-member-${u.id}`} onClick={() => openDetail(u)} className="px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-[#0B1220] transition-colors cursor-pointer">
                                        <div className="col-span-5 flex items-center gap-3 min-w-0">
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${u.role === "admin" ? "bg-[#56CCF2]/10 text-[#56CCF2]" : u.is_member ? "bg-[#2F80ED]/15 text-[#56CCF2]" : "bg-[#243044] text-[#64748B]"}`}>
                                                {u.role === "admin" ? <Shield className="w-4 h-4" /> : u.is_member ? <ShieldCheck className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-white truncate">{u.email}</p>
                                                <p className="text-xs text-[#64748B] truncate">{u.name || "—"} · {u.role || "user"}</p>
                                            </div>
                                        </div>
                                        <div className="col-span-3 text-xs">
                                            {u.subscription_status && <span className={`uppercase tracking-[0.18em] ${subColor}`}>{u.subscription_status}</span>}
                                            {u.shopify_customer_id && <div className="text-[10px] font-mono text-[#64748B] mt-0.5 truncate">cust {u.shopify_customer_id}</div>}
                                            {!u.subscription_status && !u.shopify_customer_id && <span className="text-[#64748B] italic">no shopify link</span>}
                                            {u.signup_source && <div className="text-[10px] text-[#56CCF2] mt-0.5 truncate" title={u.signup_source}>via {u.signup_source}</div>}
                                        </div>
                                        <div className="col-span-2 text-xs text-[#94A3B8]">
                                            {u.last_login_at ? <>last login<br /><span className="text-white">{formatTime(u.last_login_at)}</span></> : <span className="text-[#64748B] italic">never logged in</span>}
                                        </div>
                                        <div className="col-span-2 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                data-testid={`admin-member-magic-${u.id}`}
                                                onClick={() => sendMagicLink(u)}
                                                title="Send magic link"
                                                className="p-2 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#243044]"
                                            >
                                                <Send className="w-3.5 h-3.5" />
                                            </button>
                                            {u.role === "admin" ? (
                                                <span className="text-[10px] uppercase tracking-[0.18em] text-[#56CCF2]">admin</span>
                                            ) : (
                                                <button
                                                    data-testid={`admin-member-toggle-${u.id}`}
                                                    disabled={savingId === u.id}
                                                    onClick={() => toggle(u)}
                                                    className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors ${u.is_member ? "bg-[#56CCF2]" : "bg-[#243044]"} ${savingId === u.id ? "opacity-50" : ""}`}
                                                    aria-pressed={u.is_member}
                                                >
                                                    <span className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform ${u.is_member ? "translate-x-5" : "translate-x-0.5"}`} />
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            {selected && <MemberDetailDrawer payload={selected} onClose={() => setSelected(null)} onMagicLink={() => sendMagicLink(selected.user)} onStatusChange={(s) => setStatus(selected.user, s)} saving={savingId === selected.user.id} />}
            {showCreate && <CreateMemberModal onClose={() => setShowCreate(false)} onCreated={(u) => { setUsers((prev) => [u, ...prev]); setShowCreate(false); toast.success(`${u.email} created`); }} />}
        </AdminLayout>
    );
}

function CreateMemberModal({ onClose, onCreated }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [status, setStatus] = useState("beta");
    const [password, setPassword] = useState("");
    const [sendActivation, setSendActivation] = useState(true);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/users", {
                email: email.trim().toLowerCase(),
                name: name.trim(),
                subscription_status: status,
                password: password || undefined,
                send_activation_email: !password && sendActivation,
            });
            if (data.sent_magic_link) {
                toast.success(`Activation link sent to ${data.email}`);
            }
            onCreated(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create member");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className="w-full max-w-md bg-[#0B1220] border border-[#243044] rounded-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-create-member-modal"
            >
                <header className="px-6 pt-6 pb-4 border-b border-[#243044] flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-2 inline-flex items-center gap-1.5">
                            <UserPlus className="w-3 h-3" />
                            Manual creation
                        </div>
                        <h2 className="text-xl font-bold text-white">Add a member</h2>
                        <p className="text-xs text-[#64748B] mt-1 leading-relaxed">For gifting, founder accounts, beta onboarding, support recovery.</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-[#64748B] hover:text-white"><X className="w-4 h-4" /></button>
                </header>
                <form onSubmit={submit} className="px-6 py-5 space-y-4">
                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Email</span>
                        <input
                            data-testid="create-member-email"
                            type="email" autoFocus required
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-md bg-[#111827] border border-[#243044] text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[#2F80ED]"
                            placeholder="member@example.com"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Name <span className="text-[#475569] normal-case tracking-normal">· optional</span></span>
                        <input
                            data-testid="create-member-name"
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-md bg-[#111827] border border-[#243044] text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[#2F80ED]"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Status</span>
                        <select
                            data-testid="create-member-status"
                            value={status} onChange={(e) => setStatus(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-md bg-[#111827] border border-[#243044] text-sm text-white focus:outline-none focus:border-[#2F80ED]"
                        >
                            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Password <span className="text-[#475569] normal-case tracking-normal">· optional</span></span>
                        <input
                            data-testid="create-member-password"
                            type="text"
                            value={password} onChange={(e) => setPassword(e.target.value)}
                            placeholder="Leave blank to send activation email"
                            className="w-full px-4 py-2.5 rounded-md bg-[#111827] border border-[#243044] text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[#2F80ED]"
                        />
                    </label>
                    {!password && (
                        <label className="flex items-center gap-2 text-xs text-[#94A3B8]">
                            <input
                                data-testid="create-member-send-activation"
                                type="checkbox"
                                checked={sendActivation}
                                onChange={(e) => setSendActivation(e.target.checked)}
                                className="rounded border-[#243044] bg-[#111827]"
                            />
                            Send activation email so they can set their own password
                        </label>
                    )}
                    <div className="pt-2 flex items-center gap-3">
                        <button
                            data-testid="create-member-submit"
                            type="submit" disabled={busy}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2F80ED] hover:bg-[#1E6FE0] disabled:opacity-60 text-white text-sm font-semibold tracking-wide transition-colors"
                        >
                            {busy ? "Creating…" : "Create member"}
                        </button>
                        <button type="button" onClick={onClose} className="text-xs text-[#94A3B8] hover:text-white">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function MemberDetailDrawer({ payload, onClose, onMagicLink, onStatusChange, saving }) {
    const u = payload.user;
    const currentStatus = (u.subscription_status || "").toLowerCase();
    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="w-full max-w-2xl bg-[#0B1220] border-l border-[#243044] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-member-detail">
                <header className="px-8 py-6 border-b border-[#243044] sticky top-0 bg-[#0B1220] z-10 flex items-start justify-between gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-2">Member</div>
                        <h2 className="text-xl font-bold text-white">{u.email}</h2>
                        <p className="text-xs text-[#64748B] font-mono mt-1">{u.id}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-[#64748B] hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </header>
                <div className="px-8 py-6 space-y-6 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Name">{u.name || "—"}</Field>
                        <Field label="Role">{u.role || "user"}</Field>
                        <Field label="Subscription">{u.subscription_status ? <span className={SUB_STATUS_COLOR[u.subscription_status] || "text-[#94A3B8]"}>{u.subscription_status}</span> : "—"}</Field>
                        <Field label="Is member">{u.is_member ? <span className="text-[#56CCF2]">yes</span> : <span className="text-[#94A3B8]">no</span>}</Field>
                        <Field label="Shopify customer"><span className="font-mono">{u.shopify_customer_id || "—"}</span></Field>
                        <Field label="Signup source">{u.signup_source || "—"}</Field>
                        <Field label="Member since">{formatTime(u.created_at)}</Field>
                        <Field label="Last login">{formatTime(u.last_login_at)}</Field>
                        <Field label="Last webhook">{formatTime(u.last_webhook_at)}</Field>
                        <Field label="Renews">{formatTime(u.subscription_renews_at)}</Field>
                        {u.refunded_at && <Field label="Refunded"><span className="text-[#FB7185]">{formatTime(u.refunded_at)} · ${u.refund_amount ?? "—"}</span></Field>}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            data-testid="admin-member-detail-magic"
                            onClick={onMagicLink}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs uppercase tracking-[0.18em] bg-[#2F80ED] text-white hover:bg-[#1E6FE0]"
                        >
                            <Send className="w-3.5 h-3.5" />
                            Send magic link now
                        </button>

                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[#64748B]">Set status:</span>
                            <select
                                data-testid="admin-member-detail-status"
                                value={currentStatus || ""}
                                disabled={saving}
                                onChange={(e) => onStatusChange(e.target.value)}
                                className="px-3 py-1.5 rounded-md bg-[#111827] border border-[#243044] text-xs text-white focus:outline-none focus:border-[#2F80ED] disabled:opacity-50"
                            >
                                {!currentStatus && <option value="" disabled>—</option>}
                                {STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <Section icon={Mail} title="Magic-link history (last 10)">
                        {payload.magic_links?.length === 0 ? (
                            <Empty>No magic links sent to this address.</Empty>
                        ) : (
                            <ul className="divide-y divide-[#243044]">
                                {payload.magic_links.map((m, i) => (
                                    <li key={i} className="py-2.5 grid grid-cols-12 gap-2 items-center text-xs">
                                        <span className="col-span-5 text-[#94A3B8]">{formatTime(m.created_at)}</span>
                                        <span className="col-span-4 text-[#64748B]">→ {m.next || "/"}</span>
                                        <span className={`col-span-3 text-right uppercase tracking-[0.18em] text-[10px] ${m.consumed_at ? "text-[#56CCF2]" : "text-[#F4B731]"}`}>
                                            {m.consumed_at ? "verified" : m.admin_triggered_by ? "admin-sent" : "sent"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>

                    <Section icon={Webhook} title="Webhook history (last 20)">
                        {payload.webhook_history?.length === 0 ? (
                            <Empty>No Shopify webhook events tied to this customer yet.</Empty>
                        ) : (
                            <ul className="divide-y divide-[#243044]">
                                {payload.webhook_history.map((w, i) => (
                                    <li key={i} className="py-2.5 grid grid-cols-12 gap-2 items-center text-xs">
                                        <span className="col-span-5 font-mono text-white">{w.topic}</span>
                                        <span className="col-span-4 text-[#94A3B8]">{formatTime(w.received_at)}</span>
                                        <span className={`col-span-3 text-right uppercase tracking-[0.18em] text-[10px] ${w.status === "handled" ? "text-[#56CCF2]" : w.status === "rejected" ? "text-[#F4B731]" : w.status === "error" ? "text-[#FB7185]" : "text-[#94A3B8]"}`}>
                                            {w.status}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>

                    {payload.audit_log?.length > 0 && (
                        <Section icon={Shield} title="Admin audit trail">
                            <ul className="divide-y divide-[#243044]">
                                {payload.audit_log.map((a, i) => (
                                    <li key={i} className="py-2.5 grid grid-cols-12 gap-2 items-center text-xs">
                                        <span className="col-span-5 text-white">{a.action}</span>
                                        <span className="col-span-4 text-[#94A3B8]">by {a.actor_email}</span>
                                        <span className="col-span-3 text-right text-[#64748B]">{formatTime(a.created_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-1">{label}</div>
            <div className="text-sm text-white">{children}</div>
        </div>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#64748B] mb-3">
                <Icon className="w-3.5 h-3.5" />
                {title}
            </div>
            <div className="bg-[#111827] border border-[#243044] rounded-lg px-4">{children}</div>
        </div>
    );
}

function Empty({ children }) {
    return <div className="py-4 text-xs text-[#64748B] italic">{children}</div>;
}
